import React from 'react';
import { cn } from '@/utils/cn';

export interface MetaAccountOption {
  id: string;            // `act_<digits>` per validate_meta_ad_account_id
  businessName: string | null;
}

interface SelectMetaAccountStepProps {
  accounts: MetaAccountOption[];
  selectedAccountId: string | null;
  onSelectAccount: (accountId: string) => void;
  error?: string;
}

/**
 * Step 1 — pick which linked Meta ad account the campaign belongs to.
 *
 * The wizard auto-skips this step when the user has exactly one linked Meta
 * account (handled in the parent wizard). When 2+ accounts are linked, the
 * picker renders as a list of selectable cards.
 */
export const SelectMetaAccountStep: React.FC<SelectMetaAccountStepProps> = ({
  accounts,
  selectedAccountId,
  onSelectAccount,
  error,
}) => {
  if (accounts.length === 0) {
    return (
      <div className="py-12 text-center">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          No Linked Meta Accounts
        </h3>
        <p className="text-gray-600 mb-4">
          Link a Meta account from the Account page before creating a Meta campaign.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          Select Meta Ad Account
        </h3>
        <p className="text-sm text-gray-600">
          The campaign will be created under this account.
        </p>
      </div>

      <div role="radiogroup" aria-label="Meta ad account">
        {accounts.map((account) => (
          <button
            key={account.id}
            type="button"
            role="radio"
            aria-checked={selectedAccountId === account.id}
            onClick={() => onSelectAccount(account.id)}
            className={cn(
              'w-full text-left p-4 border-2 rounded-lg mb-2 transition-all focus:outline-none focus:ring-2 focus:ring-blue-500',
              selectedAccountId === account.id
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-200 hover:bg-gray-50 bg-white',
            )}
          >
            <div className="text-sm font-medium text-gray-900">
              {account.businessName ?? 'Unnamed business'}
            </div>
            <div className="text-xs text-gray-500 mt-1 font-mono">{account.id}</div>
          </button>
        ))}
      </div>

      {error && (
        <p role="alert" className="text-sm text-red-600 mt-2">
          {error}
        </p>
      )}
    </div>
  );
};
